// In-app alerting: when something arrives over the live stream while the app is open, ring, vibrate
// and show a tappable toast. Push covers the app being closed; this covers it being in someone's
// hand — and the two must never both fire for the same thing on the same device, which is why the
// browser suppresses push for a focused client and this handles that case instead.
//
// Deliberately narrow about what rings:
//  • a clinic hears the LAB's messages, never the echo of its own;
//  • a clinic hears "your approval is needed" and "dispatched", not every internal stage move
//    (those still refresh the screen silently);
//  • staff hear new clinic registrations.
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ROLE_AUDIENCE, WF_STAGE_ROLES } from '@alignova/shared';
import { useAuth } from '../auth/AuthContext.tsx';
import { useLiveEvent } from '../lib/useLive.ts';
import { ring, unlockAudio } from '../lib/chime.ts';
import { IconAlert, IconChat, IconClose } from '../ui/icons.tsx';

interface Toast {
  id: number;
  title: string;
  body: string;
  url: string;
  kind: 'message' | 'approval' | 'update';
}

let seq = 0;

export function LiveAlerts() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [toasts, setToasts] = useState<Toast[]>([]);
  const audience = user ? ROLE_AUDIENCE[user.role] : null;

  // Browsers refuse to play audio until the user has interacted with the page. Take the first
  // gesture anywhere and use it to unlock, so the first real alert can actually be heard.
  useEffect(() => {
    const on = () => unlockAudio();
    window.addEventListener('pointerdown', on, { once: true, passive: true });
    window.addEventListener('keydown', on, { once: true });
    return () => {
      window.removeEventListener('pointerdown', on);
      window.removeEventListener('keydown', on);
    };
  }, []);

  const show = useCallback((t: Omit<Toast, 'id'>) => {
    const id = ++seq;
    setToasts((cur) => [...cur.slice(-2), { ...t, id }]);
    ring(t.kind);
    window.setTimeout(() => setToasts((cur) => cur.filter((x) => x.id !== id)), 8000);
  }, []);

  useLiveEvent<{ caseId?: string; message?: { body?: string; side?: string; authorName?: string } }>(
    'workflow.message',
    (p) => {
      if (!p?.message) return;
      // A clinic should not be pinged by its own message coming back down the stream.
      const fromLab = p.message.side === 'lab';
      if (audience === 'clinic' && !fromLab) return;
      if (audience === 'staff' && fromLab) return;
      show({
        title: fromLab ? `${p.message.authorName ?? 'The lab'}` : `${p.message.authorName ?? 'The clinic'}`,
        body: (p.message.body ?? '').slice(0, 120),
        url: audience === 'clinic' ? `/portal/cases/${p.caseId ?? ''}` : `/workflow/cases/${p.caseId ?? ''}`,
        kind: 'message',
      });
    },
  );

  useLiveEvent<{ id?: string; stageKey?: string }>('workflow.changed', (p) => {
    if (audience !== 'clinic' || !p?.stageKey) return;
    if (p.stageKey === WF_STAGE_ROLES.doctorApproval) {
      show({
        title: 'Your approval is needed',
        body: 'The lab shared a setup for you to review.',
        url: `/portal/cases/${p.id ?? ''}`,
        kind: 'approval',
      });
    } else if (p.stageKey === WF_STAGE_ROLES.dispatch) {
      show({ title: 'Case dispatched', body: 'Your case is on its way.', url: `/portal/cases/${p.id ?? ''}`, kind: 'update' });
    }
  });

  useLiveEvent<{ name?: string; code?: string }>('clinic.registered', (p) => {
    if (audience !== 'staff') return;
    show({
      title: 'New clinic registered',
      body: `${p?.name ?? 'A clinic'}${p?.code ? ` (${p.code})` : ''} just signed up.`,
      url: '/clinics',
      kind: 'update',
    });
  });

  if (!toasts.length) return null;
  return (
    <div
      className="pointer-events-none fixed inset-x-0 top-0 z-toast flex flex-col items-center gap-2 px-3 pt-safe"
      role="status"
      aria-live="polite"
    >
      {toasts.map((t) => (
        <button
          key={t.id}
          type="button"
          onClick={() => {
            setToasts((cur) => cur.filter((x) => x.id !== t.id));
            if (t.url) navigate(t.url);
          }}
          className="animate-rise pointer-events-auto mt-2 flex w-full max-w-md items-start gap-3 rounded-lg border border-line bg-surface px-4 py-3 text-left shadow-level-3"
        >
          <span
            className={`mt-0.5 shrink-0 ${t.kind === 'approval' ? 'text-status-warn-fg' : 'text-accent'}`}
          >
            {t.kind === 'approval' ? <IconAlert size={20} /> : <IconChat size={20} />}
          </span>
          <span className="min-w-0 flex-1">
            <span className="block truncate text-sm font-semibold text-ink">{t.title}</span>
            <span className="block truncate text-sm text-muted">{t.body}</span>
          </span>
          <span
            className="shrink-0 text-muted"
            onClick={(e) => {
              e.stopPropagation();
              setToasts((cur) => cur.filter((x) => x.id !== t.id));
            }}
          >
            <IconClose size={18} />
          </span>
        </button>
      ))}
    </div>
  );
}
